// Copyright (c) 2026 Michael F. Kamprath
//
// theme-pool.js -- Pool-based theme selector for round rotation.
//
// Replicates the legacy iOS `themeMaxRepeat` selection logic
// (CONVERSION-PLAN section 4.1):
//
//   1. Build a pool containing each active theme N times (default 3)
//   2. Each new round draws randomly from the pool and removes that entry
//   3. When the pool is empty, rebuild it
//   4. If the user changes active themes, reset the pool
//
// The pool prevents the same theme appearing many rounds in a row
// (which a naive `Math.random()` would let happen) without enforcing
// strict round-robin (which would be predictable).
//
// Pure module: no PixiJS, no DOM, no globals.  An RNG can be injected
// for deterministic tests; defaults to `Math.random`.

const DEFAULT_REPEAT = 3;

/**
 * Build a fresh pool array containing each active theme `repeat` times.
 *
 * @param {string[]} activeThemes
 * @param {number} repeat
 * @returns {string[]}
 */
function buildPool(activeThemes, repeat) {
  const pool = [];
  for (const id of activeThemes) {
    for (let i = 0; i < repeat; i++) pool.push(id);
  }
  return pool;
}

/**
 * Compare two arrays of theme IDs as sets (order-insensitive).
 */
function sameActiveSet(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  const seen = new Set(a);
  for (const id of b) {
    if (!seen.has(id)) return false;
  }
  return true;
}

/**
 * Create a theme pool that draws random themes without replacement until
 * exhausted, then rebuilds.
 *
 * @param {Object} opts
 * @param {() => string[]} opts.getActiveThemes
 *   Called whenever a draw occurs.  If the active set changes between
 *   draws, the pool is rebuilt against the new set on the next draw.
 * @param {number} [opts.repeat]
 *   How many times each active theme appears in a fresh pool.  Default 3.
 * @param {() => number} [opts.rng]
 *   Returns a number in [0, 1).  Defaults to Math.random.
 */
export function createThemePool({ getActiveThemes, repeat = DEFAULT_REPEAT, rng = Math.random } = {}) {
  if (typeof getActiveThemes !== 'function') {
    throw new TypeError('createThemePool: getActiveThemes must be a function');
  }
  if (!Number.isInteger(repeat) || repeat < 1) {
    throw new TypeError(`createThemePool: repeat must be a positive integer (got ${repeat})`);
  }

  let lastActive = null;
  let pool = [];

  function ensurePool() {
    const active = getActiveThemes();
    if (!Array.isArray(active) || active.length === 0) {
      // No active themes -- caller handles the empty case via draw() returning null.
      lastActive = active ? [...active] : [];
      pool = [];
      return;
    }
    if (!sameActiveSet(active, lastActive)) {
      lastActive = [...active];
      pool = buildPool(active, repeat);
      return;
    }
    if (pool.length === 0) {
      pool = buildPool(active, repeat);
    }
  }

  return {
    /**
     * Draw a theme ID, removing it from the pool.  Rebuilds the pool if
     * empty (or if the active-themes set has changed since the last draw).
     *
     * @returns {string | null}  Theme ID, or null if no themes are active.
     */
    draw() {
      ensurePool();
      if (pool.length === 0) return null;
      const idx = Math.floor(rng() * pool.length);
      const [picked] = pool.splice(idx, 1);
      return picked;
    },

    /**
     * Force a rebuild on the next draw.  Useful when the caller knows
     * the active set has changed (e.g. settings panel just closed) and
     * wants to discard whatever's left in the current pool.
     */
    reset() {
      lastActive = null;
      pool = [];
    },

    /**
     * Inspect remaining pool size.  Mostly for tests and DEV diagnostics.
     */
    remaining() {
      return pool.length;
    },
  };
}
